import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const STASH_ATTRIBUTE_KEY = '_showroom_transfer_stash';

/**
 * @typedef {Object} SerializedLine
 * @property {number} variantId
 * @property {string} title
 * @property {Record<string, string>} selectedOptions
 * @property {number} quantity
 */

class BookingButtonComponent extends HTMLElement {
  /** @type {HTMLAnchorElement|null} */
  #link = null;

  /** @type {AbortController|null} */
  #abort = null;

  /** @type {boolean} */
  #disabled = false;

  connectedCallback() {
    this.#link = this.querySelector('a');
    if (!this.#link) return;

    this.#abort = new AbortController();

    const runRestore = () => {
      this.#maybeRestoreFromAttributes().catch(() => {});
    };

    // initial restore attempt
    runRestore();

    // re-attempt restore from BFCache
    window.addEventListener(
      'pageshow',
      (e) => {
        if (e && e.persisted) runRestore();
      },
      { signal: this.#abort.signal },
    );

    // Re-check enabled state when cart updates are emitted
    document.addEventListener(
      ThemeEvents.cartUpdate,
      () => {
        this.#updateEnabledState().catch(() => {});
      },
      { signal: this.#abort.signal },
    );

    // initial enabled/disabled evaluation
    this.#updateEnabledState().catch(() => {});

    this.#link.addEventListener(
      'click',
      (e) => {
        this.#handleClick(e).catch(() => {});
      },
      { signal: this.#abort.signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get appointmentProductId() {
    const value = this.getAttribute('data-appointment-product-id');
    return value ? Number(value) : NaN;
  }

  get appointmentProductUrl() {
    return this.getAttribute('data-appointment-product-url') || this.#link?.getAttribute('href') || '/';
  }

  async #handleClick(event) {
    if (!this.#link) return;

    if (this.#disabled) {
      event.preventDefault();
      const message =
        this.getAttribute('data-disabled-message') ||
        'Booking is not available when your cart has more than 6 items.';
      if (typeof window !== 'undefined' && window.alert) window.alert(message);
      return;
    }

    const appointmentProductId = this.appointmentProductId;
    if (!Number.isFinite(appointmentProductId)) {
      // Without an ID we can’t do cart logic; fall back to normal navigation.
      return;
    }

    event.preventDefault();

    const cart = await this.#fetchCart();

    const appointmentLines = cart.items.filter((item) => item?.product_id === appointmentProductId);
    const hasAppointment = appointmentLines.length > 0;
    const nonAppointmentLines = cart.items.filter((item) => item?.product_id !== appointmentProductId);

    // If no other products in cart (regardless of appointment presence), redirect to collection
    if (nonAppointmentLines.length === 0) {
      if (!hasAppointment) {
        // No appointment either — normal navigation to appointment PDP
        window.location.assign(this.appointmentProductUrl);
      } else {
        // Has appointment but nothing else — send to homepage
        window.location.assign('/');
      }
      return;
    }

    if (!hasAppointment) {
      // Has other products but no appointment yet — navigate to appointment PDP
      window.location.assign(this.appointmentProductUrl);
      return;
    }

    /** @type {SerializedLine[]} */
    const serialized = nonAppointmentLines.map((item) => ({
      variantId: item.variant_id,
      title: item.title,
      selectedOptions: item.properties ?? {},
      quantity: item.quantity,
    }));

    await this.#setCartAttributes({
      [STASH_ATTRIBUTE_KEY]: JSON.stringify({
        createdAt: Date.now(),
        appointmentProductId,
        lines: serialized,
      }),
    });

    await this.#updateCartQuantities(Object.fromEntries(nonAppointmentLines.map((item) => [String(item.key), 0])));

    window.location.assign('/checkout');
  }

  async #maybeRestoreFromAttributes() {
    const cart = await this.#fetchCart();
    const raw = cart?.attributes?.[STASH_ATTRIBUTE_KEY];
    if (!raw) return;

    /** @type {{ createdAt:number, appointmentProductId:number, lines:SerializedLine[] } | null} */
    let payload = null;
    try {
      payload = JSON.parse(raw);
    } catch {
      // If corrupted, clear it so it doesn’t loop forever.
      await this.#setCartAttributes({ [STASH_ATTRIBUTE_KEY]: '' });
      return;
    }

    const lines = Array.isArray(payload?.lines) ? payload.lines : [];
    if (lines.length === 0) {
      await this.#setCartAttributes({ [STASH_ATTRIBUTE_KEY]: '' });
      return;
    }

    // Restore best-effort: add variants back. Options are informational (variantId is authoritative).
    await this.#addLines(
      lines.map((line) => ({
        id: line.variantId,
        quantity: line.quantity || 1,
        properties: {
          _restored_title: line.title || '',
          _restored_options: JSON.stringify(line.selectedOptions || {}),
        },
      })),
    );

    // Clear attribute after restore to avoid repeated re-adds.
    await this.#setCartAttributes({ [STASH_ATTRIBUTE_KEY]: '' });

    const updatedCart = await this.#fetchCart();
    this.#dispatchCartUiRefresh(updatedCart);
  }

  /** Update the component enabled/disabled state and button label based on cart contents */
  async #updateEnabledState() {
    const cart = await this.#fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const count = items.length;
    const shouldDisable = count > 7;
    this.#setDisabled(shouldDisable);
    this.#updateButtonLabel(items);
  }

  /** Swap the link text based on whether there are non-appointment items in the cart */
  #updateButtonLabel(items) {
    if (!this.#link) return;
    const appointmentProductId = this.appointmentProductId;
    const labelFinalise = this.getAttribute('data-label-finalise') || '';
    const labelSelection = this.getAttribute('data-label-selection') || '';
    if (!labelFinalise && !labelSelection) return;

    const hasAppointment = items.some((item) => item?.product_id === appointmentProductId);
    const hasOtherItems = items.some((item) => item?.product_id !== appointmentProductId);

    this.#link.textContent = hasAppointment && hasOtherItems ? labelFinalise : labelSelection;
  }

  #setDisabled(disabled) {
    if (!this.#link) return;
    this.#disabled = Boolean(disabled);
    this.#link.classList.toggle('is-disabled', this.#disabled);
    if (this.#disabled) {
      this.#link.setAttribute('aria-disabled', 'true');
      this.#link.tabIndex = -1;
    } else {
      this.#link.removeAttribute('aria-disabled');
      this.#link.tabIndex = 0;
    }
  }

  /** @param {any} cart */
  #dispatchCartUiRefresh(cart) {
    // Preferred: use the theme event class so listeners receive `e.detail.resource`.
    try {
      document.dispatchEvent(new CartUpdateEvent(cart, 'booking-button-component', { source: 'booking-restore' }));
      return;
    } catch {
      // ignore
    }

    // Fallback: dispatch the known string event name.
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: { resource: cart, sourceId: 'booking-button-component', data: { source: 'booking-restore' } },
      }),
    );
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
    return res.json();
  }

  async #setCartAttributes(attributes) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error(`Failed to update cart attributes: ${res.status}`);
    return res.json();
  }

  async #updateCartQuantities(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error(`Failed to update cart quantities: ${res.status}`);
    return res.json();
  }

  async #addLines(lines) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
    });
    if (!res.ok) throw new Error(`Failed to add lines: ${res.status}`);
    return res.json();
  }
}

if (!customElements.get('booking-button-component')) {
  customElements.define('booking-button-component', BookingButtonComponent);
}
